import productConfig from 'Models/uiConfig/productConfig.js';
import { keysSelector, getRequestFromUri, getUriFromRequest } from 'Modules/converter/index.js';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig.js';
import { cloneSafe } from 'Utils/components.ts';
import Subtile from './subtile.ts';

import type { FC } from 'react';
import type { RepeaterFunctionInvoker, TemplateFunction, TemplateFunctionInvoker } from 'Components/types.ts';
import type { FacetRequest, FacetValueFull } from 'Models/index.ts';

export interface SubvalueParams {
  value: string;
  href?: string;
  getImageOrDefault: (imageUrl: string) => string;
  isMultiLevel?: boolean;
  vehicleWidget?: TemplateFunctionInvoker<unknown>;
}

export interface Params extends SubvalueParams {
  currentTab: string | null;
  subvalues: RepeaterFunctionInvoker<SubvalueParams>;
  setCurrentTab: (tile: string) => void;
  isMultiLevel?: boolean;
}

const baseParams = ['value' as const, 'term' as const, 'isSelected' as const];
type FacetValue = Pick<FacetValueFull, (typeof baseParams)[number]>;

type Props = {
  template: TemplateFunction<Params>;
  value: FacetValueFull;
  subvalues: FacetValueFull[];
  currentTab: string | null;
  isMultiLevel?: boolean;
  toggleFacet: (term: string) => void;
  useFilterMode?: boolean;
  vehicleFromUriSelection: FacetRequest[];
  vehicleWidget: TemplateFunctionInvoker<unknown>;
  setCurrentTab: (tile: string) => void;
};

const Tile: FC<Props> = ({
  template,
  value,
  subvalues,
  currentTab,
  isMultiLevel,
  toggleFacet,
  useFilterMode,
  vehicleFromUriSelection,
  vehicleWidget,
  setCurrentTab,
}) => {
  const subvaluesMap = subvalues.map((subvalue) => (templ) => {
    const props = {
      template: templ,
      toggleFacet,
      useFilterMode,
      subvalue,
      vehicleFromUriSelection,
      vehicleWidget,
    };

    return <Subtile {...props} key={subvalue.term} />;
  }) as RepeaterFunctionInvoker<SubvalueParams>;

  const component = template.call({
    ...getParams(value, vehicleFromUriSelection),
    subvalues: subvaluesMap,
    currentTab,
    isMultiLevel,
    getImageOrDefault,
    setCurrentTab,
    vehicleWidget,
  });

  const options = useFilterMode
    ? {
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          toggleFacet(value.term);
        },
      }
    : {};

  return cloneSafe(component, null, { ...options });
};

export function getImageOrDefault(imageUrl: string): string {
  return imageUrl || productConfig.noImageSrc;
}

export function getParams(
  value: FacetValueFull,
  vehicleFromUriSelection?: FacetRequest[],
): FacetValue & { imageUrl: FacetValueFull['imageUrl']; href: string | undefined; payload: string } {
  const { imageUrl, payload } = value;

  return {
    ...keysSelector<FacetValueFull, FacetValue>(...baseParams)(value),
    href: getHref(value, vehicleFromUriSelection),
    imageUrl,
    payload: payload || '',
  };
}

function getHref(value: FacetValueFull, vehicleFromUriSelection?: FacetRequest[]): string {
  const { url: valueUrl } = value;
  const url = valueUrl ? (valueUrl.includes('/') ? valueUrl : `${window.location.href}/${valueUrl}`) : '';

  let href = '';

  if (vehicleFromUriSelection?.length) {
    if (url) {
      let { selection } = getRequestFromUri(new URL(url, window.location.origin));

      if (
        !selection.some(
          (f: FacetRequest) => f.field === 'Vehicle' || fitmentSearchConfig.fields.includes(f.field),
        )
      ) {
        selection = [...selection, ...vehicleFromUriSelection];
      }

      ({ href } = getUriFromRequest({ selection }, { pathname: new URL(url, window.location.origin).href }));
    } else {
      ({ href } = getUriFromRequest(
        { selection: [value, ...vehicleFromUriSelection] },
        { goToSearchPage: true },
      ));
    }
  } else {
    href = url ? url : getUriFromRequest({ selection: [value] }, { goToSearchPage: true }).href;
  }

  return href;
}

export default Tile;
